/**
 * QRShareModal — a single reusable modal that renders a QR code + the raw
 * URL for any shareable resource (wishlist, comparison, product PDP, etc.).
 *
 * Prefer this over ad-hoc `alert(url)` UX — the QR turns any share link
 * into an in-person hand-off (point-your-phone-at-my-screen).
 *
 * Usage:
 *   const [open, setOpen] = useState(false)
 *   <QRShareModal open={open} onOpenChange={setOpen}
 *                 url="https://.../compare?ids=…"
 *                 title="Compare 3 products"
 *                 subtitle="Point a phone camera to load the shortlist." />
 */
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const QRShareModal: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  url: string;
  title?: string;
  subtitle?: string;
  testIdPrefix?: string;
}> = ({ open, onOpenChange, url, title = "Share this link", subtitle, testIdPrefix = "qr-share" }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast("Link copied to clipboard", { duration: 1500 });
    } catch {
      toast("Copy failed — long-press the link to copy manually");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-white dark:bg-slate-900" data-testid={testIdPrefix}>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-slate-900 dark:text-white tracking-tight">
            {title}
          </DialogTitle>
          {subtitle && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>
          )}
        </DialogHeader>

        <div className="flex justify-center py-4">
          <div
            className="bg-white p-3 border-4 border-slate-900 dark:border-white shadow-xl"
            data-testid={`${testIdPrefix}-qr`}
          >
            <QRCodeSVG
              value={url}
              size={220}
              level="M"
              includeMargin={false}
              fgColor="#0F172A"
              bgColor="#FFFFFF"
            />
          </div>
        </div>

        <div className="rounded-none border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-display font-bold mb-1">
            or send this link
          </div>
          <div
            className="font-mono text-xs text-slate-700 dark:text-slate-300 break-all leading-relaxed"
            data-testid={`${testIdPrefix}-url`}
          >
            {url}
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button
            onClick={copy}
            data-testid={`${testIdPrefix}-copy`}
            className="flex-1 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 font-display font-semibold"
          >
            {copied ? <Check className="h-4 w-4 mr-1.5 text-emerald-400" /> : <Copy className="h-4 w-4 mr-1.5" />}
            {copied ? "Copied!" : "Copy link"}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(url, "_blank", "noopener")}
            data-testid={`${testIdPrefix}-open`}
            className="font-display font-semibold border-slate-200 dark:border-slate-700"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRShareModal;
